import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const QuickExportMenu = ({ className = '', data = null, onDataReady = null }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [exportError, setExportError] = useState('');

  const getExportOptions = () => {
    const currentPath = location?.pathname;
    
    const baseOptions = [
      {
        id: 'pdf-report',
        label: 'PDF Report',
        icon: 'FileText',
        description: 'Complete dashboard report'
      },
      {
        id: 'csv-data',
        label: 'CSV Data',
        icon: 'Download',
        description: 'Raw data export'
      },
      {
        id: 'json-data',
        label: 'JSON Data',
        icon: 'Code',
        description: 'Structured data export'
      }
    ];

    // Add context-specific options based on current page
    switch (currentPath) {
      case '/': case'/main-analytics-dashboard':
        return [
          ...baseOptions,
          {
            id: 'kpi-summary',
            label: 'KPI Summary',
            icon: 'BarChart3',
            description: 'Key performance indicators'
          }
        ];
      case '/geographic-performance':
        return [
          ...baseOptions,
          {
            id: 'geographic-data',
            label: 'Geographic Data',
            icon: 'MapPin',
            description: 'Location-based analytics'
          }
        ];
      case '/campaign-management-hub':
        return [
          ...baseOptions,
          {
            id: 'campaign-report',
            label: 'Campaign Report',
            icon: 'Target',
            description: 'Campaign performance data'
          }
        ];
      case '/user-activity-monitor':
        return [
          ...baseOptions,
          {
            id: 'activity-log',
            label: 'Activity Log',
            icon: 'Users',
            description: 'User activity data'
          }
        ];
      case '/qr-code-library':
        return [
          ...baseOptions,
          {
            id: 'qr-list',
            label: 'QR Code List',
            icon: 'QrCode',
            description: 'QR codes and analytics'
          }
        ];
      default:
        return baseOptions;
    }
  };

  const generateCSV = (exportData) => {
    if (!exportData || !Array.isArray(exportData)) return '';
    
    if (exportData?.length === 0) return '';
    
    // Get headers from first object
    const headers = Object.keys(exportData?.[0]);
    const csvHeaders = headers?.join(',');
    
    // Convert data rows
    const csvRows = exportData?.map(row => {
      return headers?.map(header => {
        const value = row?.[header];
        // Handle values that contain commas or quotes
        if (typeof value === 'string' && (value?.includes(',') || value?.includes('"'))) {
          return `"${value?.replace(/"/g, '""')}"`;
        }
        return value || '';
      })?.join(',');
    });
    
    return [csvHeaders, ...csvRows]?.join('\n');
  };

  const generateJSON = (exportData) => {
    return JSON.stringify({
      exportedAt: new Date()?.toISOString(),
      source: location?.pathname,
      data: exportData
    }, null, 2);
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getExportData = async () => {
    try {
      if (onDataReady) {
        return await onDataReady();
      }

      // Return empty data if no callback provided - let components provide their own data
      return [];
    } catch (error) {
      throw new Error(`Failed to prepare export data: ${error.message}`);
    }
  };

  const handleExport = async (optionId) => {
    setIsExporting(true);
    setExportError('');
    
    try {
      const exportData = await getExportData();
      const timestamp = new Date()?.toISOString()?.split('T')?.[0];
      const pageTitle = location?.pathname?.replace('/', '') || 'analytics';
      
      let content, filename, mimeType;

      switch (optionId) {
        case 'csv-data': case'kpi-summary': case'campaign-report': case'activity-log': case'qr-list': case'geographic-data':
          content = generateCSV(exportData);
          filename = `${pageTitle}-${timestamp}.csv`;
          mimeType = 'text/csv;charset=utf-8;';
          break;

        case 'json-data':
          content = generateJSON(exportData);
          filename = `${pageTitle}-${timestamp}.json`;
          mimeType = 'application/json;charset=utf-8;';
          break;

        case 'pdf-report':
          // For PDF, generate a simple text report
          const reportData = Array.isArray(exportData) 
            ? exportData?.map(item => JSON.stringify(item, null, 2))?.join('\n\n')
            : JSON.stringify(exportData, null, 2);
          
          content = `QR Analytics Report\n\nGenerated: ${new Date()?.toLocaleString()}\nPage: ${pageTitle}\n\n${reportData}`;
          filename = `${pageTitle}-report-${timestamp}.txt`;
          mimeType = 'text/plain;charset=utf-8;';
          break;

        default:
          throw new Error('Unsupported export format');
      }

      if (!content) {
        throw new Error('No data available for export');
      }

      downloadFile(content, filename, mimeType);
      
      // Show success feedback
      setIsOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      setExportError(`Export failed: ${error?.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    setExportError('');
  };

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event?.target?.closest('.export-menu')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const exportOptions = getExportOptions();

  if (isMobile) {
    return (
      <>
        {/* Mobile FAB */}
        <div className={`fixed bottom-20 right-4 z-40 export-menu ${className}`}>
          <Button
            variant="default"
            size="icon"
            className="w-14 h-14 rounded-full shadow-lg"
            onClick={toggleMenu}
            disabled={isExporting}
          >
            {isExporting ? (
              <Icon name="Loader2" size={24} className="animate-spin" />
            ) : (
              <Icon name={isOpen ? 'X' : 'Download'} size={24} />
            )}
          </Button>
        </div>
        {/* Mobile Export Menu */}
        {isOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-end">
            <div className="w-full bg-white rounded-t-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Export Options</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>

              {exportError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{exportError}</p>
                </div>
              )}
              
              <div className="space-y-2">
                {exportOptions?.map((option) => (
                  <Button
                    key={option?.id}
                    variant="ghost"
                    fullWidth
                    className="justify-start p-4 h-auto"
                    onClick={() => handleExport(option?.id)}
                    disabled={isExporting}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                        <Icon name={option?.icon} size={20} className="text-blue-600" />
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-gray-900">{option?.label}</span>
                        <span className="text-sm text-gray-500">{option?.description}</span>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-40 export-menu ${className}`}>
      {/* Desktop FAB */}
      <div className="relative">
        <Button
          variant="default"
          size="icon"
          className="w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200"
          onClick={toggleMenu}
          disabled={isExporting}
        >
          {isExporting ? (
            <Icon name="Loader2" size={24} className="animate-spin" />
          ) : (
            <Icon name={isOpen ? 'X' : 'Download'} size={24} />
          )}
        </Button>

        {/* Desktop Export Menu */}
        {isOpen && (
          <div className="absolute bottom-full right-0 mb-4 w-64 bg-white border border-gray-200 rounded-lg shadow-lg">
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Export Options</h3>

              {exportError && (
                <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-xs text-red-600">{exportError}</p>
                </div>
              )}
              
              <div className="space-y-1">
                {exportOptions?.map((option) => (
                  <Button
                    key={option?.id}
                    variant="ghost"
                    size="sm"
                    fullWidth
                    className="justify-start p-3 h-auto hover:bg-gray-50"
                    onClick={() => handleExport(option?.id)}
                    disabled={isExporting}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-md flex items-center justify-center">
                        <Icon name={option?.icon} size={16} className="text-blue-600" />
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-gray-900">{option?.label}</span>
                        <span className="text-xs text-gray-500">{option?.description}</span>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Export Progress Indicator */}
      {isExporting && (
        <div className="absolute -top-16 right-0 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-lg">
          <div className="flex items-center space-x-2">
            <Icon name="Loader2" size={16} className="animate-spin text-blue-600" />
            <span className="text-sm text-gray-900">Exporting...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickExportMenu;